import { remCSS, useTranslation } from '@gimlite/common';
import classNames from 'classnames';
import { observer } from 'mobx-react-lite';
import { memo, useRef } from 'react';
import { useStoreUIV2 } from '../../hooks/useStore.hook';
import { Icon } from '../icon/icon.component';
import { Text, TextType } from '../text/text.component';
import './avatar.component.scss';

const sizeMapper = {
  small: {
    size: remCSS(32),
    textVariant: 'label',
  },
  medium: {
    size: remCSS(56),
    textVariant: 'headline',
  },
  large: {
    size: remCSS(100),
    textVariant: 'large-title',
  },
} as const satisfies Record<
  string,
  { size: string; textVariant: TextType.Props['variant'] }
>;

export declare namespace AvatarType {
  type Props = {
    className?: string | null;
    firstName?: string | null;
    lastName?: string | null;
    image?: string | null;
    isEditable?: boolean;
    onEdit?: (base64String: string) => void;
    size?: keyof typeof sizeMapper;
  };
}

const MAX_FILE_SIZE = 1 * 1024 * 1024;
const ACCEPTED_FILE_TYPES = ['image/jpeg', 'image/png', 'image/jpg'];

export const Avatar = memo(
  observer(
    ({
      firstName,
      lastName,
      image,
      className,
      isEditable = false,
      size = 'small',
      onEdit,
    }: AvatarType.Props) => {
      const inputRef = useRef<HTMLInputElement>(null);
      const { t } = useTranslation();
      const { NotificationStore } = useStoreUIV2();

      const validateFile = (file: File): boolean => {
        if (!ACCEPTED_FILE_TYPES.includes(file.type)) {
          NotificationStore.emit({
            mode: 'ERROR',
            content: t('watermelon-invalidFileType'),
          });
          return false;
        }

        if (file.size > MAX_FILE_SIZE) {
          NotificationStore.emit({
            mode: 'ERROR',
            content: t('watermelon-fileTooLarge'),
          });
          return false;
        }

        return true;
      };

      return (
        <div
          className={classNames(`avatar`, className)}
          style={{
            width: sizeMapper[size].size,
            height: sizeMapper[size].size,
            cursor: isEditable ? 'pointer' : 'default',
          }}
        >
          {isEditable && (
            <div
              onClick={() => inputRef.current?.click()}
              className="avatar__editable"
            >
              <div className="avatar__editable__mask"></div>
              <input
                ref={inputRef}
                type="file"
                accept={ACCEPTED_FILE_TYPES.join(',')}
                style={{ display: 'none' }}
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) {
                    if (!validateFile(file)) {
                      if (inputRef.current) {
                        inputRef.current.value = '';
                      }
                      return;
                    }

                    const reader = new FileReader();
                    reader.onloadend = () => {
                      const base64String = reader.result as string;
                      onEdit?.(base64String);
                    };
                    reader.readAsDataURL(file);
                  }
                }}
              />
              <Icon
                className="avatar__editable__icon"
                variant="camera"
                color={'white_100'}
              />
            </div>
          )}
          {image ? (
            <img src={image} />
          ) : (
            <Text variant={sizeMapper[size].textVariant} color="black_2">
              {firstName ? firstName[0] : ''}
              {lastName ? lastName[0] : ''}
            </Text>
          )}
        </div>
      );
    },
  ),
);
